/**
 * VendorsPage.tsx
 *
 * Vendor management page for the inventory module.
 * Supports listing, searching, filtering, creating, editing, and soft-deleting vendors.
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listVendors,
  createVendor,
  updateVendor,
  deleteVendor,
  type Vendor,
  type CreateVendorPayload,
  type UpdateVendorPayload,
} from '@/services/vendors.service';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableAction, DataTableColumn } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/Button';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { usePageContext } from '@/hooks/usePageContext';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';
import {
  Download,
  Plus,
  X,
  Loader2,
} from 'lucide-react';

// ─── Constants ─────────────────────────────────────────────────────────────────

const PER_PAGE = 25;
const DEBOUNCE_MS = 300;

// ─── Filter configs ────────────────────────────────────────────────────────────

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search vendors...' },
  { key: 'status', label: 'Status', type: 'select', allLabel: 'All statuses' },
];

// ─── Form state ────────────────────────────────────────────────────────────────

interface VendorFormState {
  name: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
}

const EMPTY_FORM: VendorFormState = {
  name: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
};

function vendorToForm(vendor: Vendor): VendorFormState {
  return {
    name: vendor.name,
    contactName: vendor.contactName ?? '',
    email: vendor.email ?? '',
    phone: vendor.phone ?? '',
    address: vendor.address ?? '',
    notes: vendor.notes ?? '',
  };
}

// ─── Vendor Modal ──────────────────────────────────────────────────────────────

interface VendorModalProps {
  vendor?: Vendor | null;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (form: VendorFormState) => void;
}

function VendorModal({ vendor, saving, error, onClose, onSubmit }: VendorModalProps) {
  const isEdit = vendor != null;
  const [form, setForm] = useState<VendorFormState>(() =>
    isEdit ? vendorToForm(vendor) : EMPTY_FORM,
  );

  function set<K extends keyof VendorFormState>(field: K, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSubmit(form);
  }

  const labelCls = 'block text-sm font-medium text-slate-700 mb-1';
  const inputCls =
    'block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : onClose} />

      {/* Card */}
      <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEdit ? 'Edit Vendor' : 'Add Vendor'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form id="vendor-form" onSubmit={handleSubmit} className="overflow-y-auto px-6 py-5 flex-1 space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label htmlFor="vendor-name" className={labelCls}>
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="vendor-name"
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className={inputCls}
              placeholder="e.g. Acme Medical Supplies"
              required
              autoFocus
              disabled={saving}
            />
          </div>

          {/* Contact Name */}
          <div>
            <label htmlFor="vendor-contact" className={labelCls}>Contact Name</label>
            <input
              id="vendor-contact"
              type="text"
              value={form.contactName}
              onChange={(e) => set('contactName', e.target.value)}
              className={inputCls}
              placeholder="e.g. Jane Smith"
              disabled={saving}
            />
          </div>

          {/* Email & Phone row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="vendor-email" className={labelCls}>Email</label>
              <input
                id="vendor-email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                className={inputCls}
                placeholder="vendor@example.com"
                disabled={saving}
              />
            </div>
            <div>
              <label htmlFor="vendor-phone" className={labelCls}>Phone</label>
              <input
                id="vendor-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                className={inputCls}
                placeholder="(555) 123-4567"
                disabled={saving}
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label htmlFor="vendor-address" className={labelCls}>Address</label>
            <textarea
              id="vendor-address"
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              className={inputCls}
              rows={2}
              placeholder="Street, City, State, ZIP"
              disabled={saving}
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="vendor-notes" className={labelCls}>Notes</label>
            <textarea
              id="vendor-notes"
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className={inputCls}
              rows={3}
              placeholder="Optional internal notes..."
              disabled={saving}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-md hover:bg-slate-50 text-slate-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="vendor-form"
            disabled={saving || !form.name.trim()}
            className="inline-flex items-center gap-1.5 bg-primary text-white hover:bg-primary/90 rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Add Vendor'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Confirm Dialog ─────────────────────────────────────────────────────

interface DeleteConfirmProps {
  vendor: Vendor;
  deleting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

function DeleteConfirmDialog({ vendor, deleting, onConfirm, onClose }: DeleteConfirmProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={deleting ? undefined : onClose} />
      <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-slate-900">Delete Vendor</h3>
        <p className="mt-2 text-sm text-slate-600">
          Are you sure you want to delete <span className="font-medium text-slate-900">"{vendor.name}"</span>?
          This action cannot be undone.
        </p>
        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-md hover:bg-slate-50 text-slate-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 bg-red-600 text-white hover:bg-red-700 rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function VendorsPage() {
  const queryClient = useQueryClient();
  const updateAIContext = usePageContext('Vendors');

  // ── Local state ──────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editVendor, setEditVendor] = useState<Vendor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Vendor | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const { exporting, startExport, exportModal } = useAsyncExport({ entityType: 'vendors', entityLabel: 'vendors' });

  // ── Debounced search ─────────────────────────────────────────────────────
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // ── Build active param from filter ───────────────────────────────────────
  const activeParam = useMemo(() => {
    if (activeFilter === 'active') return true;
    if (activeFilter === 'inactive') return false;
    return undefined;
  }, [activeFilter]);

  // ── Queries ──────────────────────────────────────────────────────────────
  const {
    data,
    isLoading,
  } = useQuery({
    queryKey: ['vendors-list', page, debouncedSearch, activeFilter],
    queryFn: () =>
      listVendors({
        search: debouncedSearch || undefined,
        active: activeParam,
        page,
        perPage: PER_PAGE,
      }),
    placeholderData: (prev) => prev,
  });

  // Stats query: fetch all vendors (no search filter) to get total/active counts
  const { data: statsData } = useQuery({
    queryKey: ['vendors-stats'],
    queryFn: () => listVendors({ perPage: 1 }),
    staleTime: 30_000,
  });

  const { data: activeStatsData } = useQuery({
    queryKey: ['vendors-stats-active'],
    queryFn: () => listVendors({ active: true, perPage: 1 }),
    staleTime: 30_000,
  });

  const vendors = data?.data ?? [];
  const meta = data?.meta;
  const totalVendors = statsData?.meta.total ?? 0;
  const activeVendors = activeStatsData?.meta.total ?? 0;

  function handleExport() {
    startExport({ search: debouncedSearch || undefined, active: activeParam });
  }

  // ── Sidebar filter plumbing ──────────────────────────────────────────────

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(() => ({
    status: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
    ],
  }), []);

  const sidebarValues = useMemo<Record<string, FilterValue>>(() => ({
    search: debouncedSearch ? [debouncedSearch] : [],
    status: activeFilter ? [activeFilter] : [],
  }), [debouncedSearch, activeFilter]);

  const handleSidebarChange = useCallback((key: string, value: FilterValue) => {
    switch (key) {
      case 'search':
        setSearch(Array.isArray(value) && value[0] ? value[0] : '');
        break;
      case 'status': {
        const v = Array.isArray(value) && value[0] ? value[0] as string : '';
        setActiveFilter(v);
        setPage(1);
        break;
      }
    }
  }, []);

  const handleSidebarClear = useCallback((key: string) => {
    switch (key) {
      case 'search':
        setSearch('');
        break;
      case 'status':
        setActiveFilter('');
        break;
    }
  }, []);

  // ── AI Page Context ──────────────────────────────────────────────────────
  useEffect(() => {
    const parts: string[] = [];
    if (activeFilter) parts.push(`status=${activeFilter}`);
    if (debouncedSearch) parts.push(`search="${debouncedSearch}"`);

    updateAIContext({
      filters: parts.length ? parts.join(', ') : 'none (showing all)',
      dataSummary: `${vendors.length} shown of ${meta?.total ?? 0} total, page ${page}/${meta?.totalPages ?? 1}`,
    });
  }, [activeFilter, debouncedSearch, vendors.length, meta?.total, meta?.totalPages, page, updateAIContext]);

  // ── Mutations ────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: createVendor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors-list'] });
      queryClient.invalidateQueries({ queryKey: ['vendors-stats'] });
      queryClient.invalidateQueries({ queryKey: ['vendors-stats-active'] });
      setShowCreateModal(false);
      setFormError(null);
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : 'Failed to create vendor.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateVendorPayload }) =>
      updateVendor(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors-list'] });
      queryClient.invalidateQueries({ queryKey: ['vendors-stats'] });
      queryClient.invalidateQueries({ queryKey: ['vendors-stats-active'] });
      setEditVendor(null);
      setFormError(null);
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : 'Failed to update vendor.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteVendor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors-list'] });
      queryClient.invalidateQueries({ queryKey: ['vendors-stats'] });
      queryClient.invalidateQueries({ queryKey: ['vendors-stats-active'] });
      setDeleteTarget(null);
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleCreate = useCallback(
    (form: VendorFormState) => {
      const payload: CreateVendorPayload = {
        name: form.name.trim(),
        contactName: form.contactName.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
        notes: form.notes.trim() || undefined,
      };
      createMutation.mutate(payload);
    },
    [createMutation],
  );

  const handleUpdate = useCallback(
    (form: VendorFormState) => {
      if (!editVendor) return;
      const payload: UpdateVendorPayload = {
        name: form.name.trim(),
        contactName: form.contactName.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
        notes: form.notes.trim() || undefined,
      };
      updateMutation.mutate({ id: editVendor.vendorId, payload });
    },
    [editVendor, updateMutation],
  );

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.vendorId);
  }, [deleteTarget, deleteMutation]);

  const handleCloseCreate = useCallback(() => {
    if (createMutation.isPending) return;
    setShowCreateModal(false);
    setFormError(null);
  }, [createMutation.isPending]);

  const handleCloseEdit = useCallback(() => {
    if (updateMutation.isPending) return;
    setEditVendor(null);
    setFormError(null);
  }, [updateMutation.isPending]);

  // ── DataTable columns ────────────────────────────────────────────────────

  const columns = useMemo<DataTableColumn<Vendor>[]>(() => [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (vendor) => (
        <span className="font-medium text-slate-900">{vendor.name}</span>
      ),
    },
    {
      key: 'contactName',
      header: 'Contact Name',
      render: (vendor) => (
        <span className="text-slate-600">{vendor.contactName || '--'}</span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (vendor) =>
        vendor.email ? (
          <a
            href={`mailto:${vendor.email}`}
            className="text-blue-600 hover:text-blue-700 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {vendor.email}
          </a>
        ) : (
          <span className="text-slate-400">--</span>
        ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (vendor) => (
        <span className="text-slate-600">{vendor.phone || '--'}</span>
      ),
    },
    {
      key: 'active',
      header: 'Status',
      render: (vendor) =>
        vendor.active ? (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700">
            Active
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600">
            Inactive
          </span>
        ),
    },
  ], []);

  const rowActions = useMemo<DataTableAction<Vendor>[]>(() => [
    {
      label: 'Edit',
      onClick: (vendor) => {
        setFormError(null);
        setEditVendor(vendor);
      },
    },
    {
      label: 'Delete',
      onClick: (vendor) => setDeleteTarget(vendor),
      variant: 'danger',
    },
  ], []);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={sidebarValues}
            options={sidebarOptions}
            onChange={handleSidebarChange}
            onClear={handleSidebarClear}
            showAIAssistant={false}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              variant="compact"
              title="Vendors"
              actions={
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    leftIcon={<Download className="h-4 w-4" />}
                    disabled={(meta?.total ?? 0) === 0 || exporting}
                    loading={exporting}
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                  {exportModal}
                  <Button
                    variant="primary"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => {
                      setFormError(null);
                      setShowCreateModal(true);
                    }}
                  >
                    Add Vendor
                  </Button>
                </div>
              }
            />

            <MetricCardGrid columns={2}>
              <StatCard
                label="Total Vendors"
                value={totalVendors}
              />
              <StatCard
                label="Active Vendors"
                value={activeVendors}
              />
            </MetricCardGrid>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<Vendor>
              data={vendors}
              columns={columns}
              keyField="vendorId"
              loading={isLoading}
              rowActions={rowActions}
              page={page}
              pageSize={PER_PAGE}
              total={meta?.total ?? 0}
              onPageChange={setPage}
              emptyTitle="No vendors found"
              emptyDescription={
                debouncedSearch
                  ? 'Try adjusting your search terms.'
                  : 'Vendors are who you place purchase orders with. Add one to start ordering.'
              }
            />
          </div>
        </div>
      </ListPageLayout>

      {/* Create modal */}
      {showCreateModal && (
        <VendorModal
          saving={createMutation.isPending}
          error={formError}
          onClose={handleCloseCreate}
          onSubmit={handleCreate}
        />
      )}

      {/* Edit modal */}
      {editVendor && (
        <VendorModal
          vendor={editVendor}
          saving={updateMutation.isPending}
          error={formError}
          onClose={handleCloseEdit}
          onSubmit={handleUpdate}
        />
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <DeleteConfirmDialog
          vendor={deleteTarget}
          deleting={deleteMutation.isPending}
          onConfirm={handleDelete}
          onClose={() => {
            if (!deleteMutation.isPending) setDeleteTarget(null);
          }}
        />
      )}
    </>
  );
}
